// src/pages/Buyer/Shipping.jsx
// ✅ COMPLETE: Shows all buyer shipments with detail view and stepper progress
import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Alert, CircularProgress, Stepper, Step, StepLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, LinearProgress, Divider, Avatar, Tooltip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  LocalShipping, LocationOn, CalendarToday, Refresh,
  CheckCircle, Schedule, ArrowBack, Visibility,
  Assignment, FlightTakeoff, Anchor, DirectionsBoat,
  Download,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { formatDate, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';
import api from '../../services/api';

const STEPS = ['Booked', 'In Transit', 'At Port', 'Out for Delivery', 'Delivered'];
const STEP_KEYS = ['pending', 'in_transit', 'at_port', 'out_for_delivery', 'delivered'];
const STEP_ICONS = [
  <Assignment />, <LocalShipping />, <Anchor />, <FlightTakeoff />, <CheckCircle />
];

const getStepIndex = (status) => {
  const idx = STEP_KEYS.indexOf(status);
  return idx >= 0 ? idx : (status === 'shipped' ? 1 : 0);
};

const STATUS_CHIP_COLOR = {
  pending:     'default',
  shipped:     'primary',
  in_transit:  'info',
  arrived:     'warning',
  delivered:   'success',
};

const BuyerShipping = () => {
  const navigate = useNavigate();
  const [shipments, setShipments] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null);
  const [detail,    setDetail]    = useState(false);

  // Stats
  const stats = {
    total:      shipments.length,
    inTransit:  shipments.filter(s => ['shipped','in_transit'].includes(s.status)).length,
    delivered:  shipments.filter(s => s.status === 'delivered').length,
    pending:    shipments.filter(s => s.status === 'pending').length,
  };

  useEffect(() => { fetchShipments(); }, []);

  const fetchShipments = async () => {
    setLoading(true);
    try {
      // Try dedicated buyer shipments endpoint
      let data = [];
      try {
        const { default: apiModule } = await import('../../services/api');
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/buyer/shipments`,
          { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
        );
        if (res.ok) {
          const json = await res.json();
          data = json.shipments || json || [];
        }
      } catch (e) {
        console.warn('buyer/shipments fetch error', e);
      }

      // Fallback: load from buyer enquiries
      if (!data.length) {
        try {
          const token = localStorage.getItem('token');
          const res2 = await fetch(
            `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/buyer/enquiries`,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
          );
          if (res2.ok) {
            const enqs = await res2.json();
            const enqList = Array.isArray(enqs) ? enqs : enqs.enquiries || [];
            // For each enquiry that has a shipment, fetch it
            const shipmentPromises = enqList
              .filter(e => ['shipped','confirmed','invoice_sent','completed'].includes(e.status))
              .map(async (e) => {
                try {
                  const sr = await fetch(
                    `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/shipping/enquiry/${e.id}`,
                    { headers: { Authorization: `Bearer ${token}` } }
                  );
                  if (sr.ok) {
                    const sd = await sr.json();
                    return { ...sd, product: e.product || e.productName, quantity: e.quantity };
                  }
                } catch { /* skip */ }
                return null;
              });
            const results = await Promise.all(shipmentPromises);
            data = results.filter(Boolean);
          }
        } catch (e2) {
          console.warn('fallback fetch error', e2);
        }
      }

      setShipments(data);
    } catch (err) {
      toast.error('Failed to load shipments');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (s) => { setSelected(s); setDetail(true); };

  // BL document download URL
  const BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace(/\/api$/, '');
  const getBlDocUrl = (s) => {
    if (!s) return null;
    // Check documents array first — look for bl or bill_of_lading type
    if (s.documents && s.documents.length > 0) {
      const blDoc = s.documents.find(d =>
        ['bl','bill_of_lading','BL','bill of lading'].includes((d.doc_type || '').toLowerCase())
      ) || s.documents[0]; // fallback to first doc
      if (blDoc && blDoc.url) return `${BASE_URL}${blDoc.url}`;
    }
    // Fallback to document_url field
    if (s.document_url) {
      return s.document_url.startsWith('http') ? s.document_url : `${BASE_URL}${s.document_url}`;
    }
    return null;
  };

  // Arrival countdown
  const getArrivalInfo = (s) => {
    if (!s.expected_arrival) return null;
    const days = Math.ceil((new Date(s.expected_arrival) - new Date()) / 86400000);
    if (days < 0) return { label: `${Math.abs(days)}d overdue`, color: 'error' };
    if (days === 0) return { label: 'Arriving today!', color: 'success' };
    return { label: `${days} days to arrival`, color: days <= 7 ? 'warning' : 'info' };
  };

  if (loading) return (
    <Container sx={{ py: 4 }}>
      <LinearProgress sx={{ mb: 2 }} />
      <Typography color="text.secondary">Loading shipments...</Typography>
    </Container>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">My Shipments</Typography>
          <Typography color="text.secondary">Track your fruit shipments in real time</Typography>
        </Box>
        <Button variant="contained" startIcon={<Refresh />} onClick={fetchShipments}>Refresh</Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Shipments', value: stats.total,     color: '#2196f3', icon: <LocalShipping /> },
          { label: 'In Transit',      value: stats.inTransit, color: '#ff9800', icon: <DirectionsBoat />, textColor: 'warning.main' },
          { label: 'Delivered',       value: stats.delivered, color: '#4caf50', icon: <CheckCircle />,    textColor: 'success.main' },
          { label: 'Booked',          value: stats.pending,   color: '#9c27b0', icon: <Schedule />,       textColor: 'secondary.main' },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">{s.label}</Typography>
                    <Typography variant="h4" fontWeight="bold" color={s.textColor || 'text.primary'}>
                      {s.value}
                    </Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${s.color}20`, color: s.color }}>{s.icon}</Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Shipments Table */}
      {shipments.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
          <LocalShipping sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>No shipments yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Shipments appear once admin creates them for your confirmed orders.
          </Typography>
          <Button variant="outlined" onClick={() => navigate('/buyer/enquiries')}>
            View My Orders
          </Button>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  {['Shipment ID','Product','Container / BL','Route','Arrival','Status','Actions'].map(h => (
                    <TableCell key={h}><Typography variant="subtitle2" fontWeight="bold">{h}</Typography></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {shipments.map((s) => {
                  const arr = getArrivalInfo(s);
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell><Typography variant="body2" fontWeight="medium">#{s.id}</Typography></TableCell>
                      <TableCell>
                        <Typography variant="body2">{s.product || '—'}</Typography>
                        {s.quantity && <Typography variant="caption" color="text.secondary">{s.quantity} containers</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{s.container_number || '—'}</Typography>
                        {s.bl_number && <Typography variant="caption" color="text.secondary">BL: {s.bl_number}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {s.port_of_loading || '—'} → {s.port_of_discharge || '—'}
                        </Typography>
                        {s.vessel_name && <Typography variant="caption" color="text.secondary">{s.vessel_name}</Typography>}
                      </TableCell>
                      <TableCell>
                        {arr ? (
                          <Chip label={arr.label} size="small" color={arr.color} variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(s.expected_arrival) || '—'}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={(s.status || 'pending').replace(/_/g, ' ')}
                          size="small"
                          color={STATUS_CHIP_COLOR[s.status] || 'default'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View Details">
                          <IconButton size="small" color="primary" onClick={() => openDetail(s)}>
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        {s.tracking_url && (
                          <Tooltip title="Track Online">
                            <IconButton size="small" color="secondary"
                              onClick={() => window.open(s.tracking_url, '_blank')}>
                              <LocationOn />
                            </IconButton>
                          </Tooltip>
                        )}
                        {getBlDocUrl(s) && (
                          <Tooltip title="Download BL Document">
                            <IconButton size="small" color="success"
                              onClick={() => window.open(getBlDocUrl(s), '_blank')}>
                              <Download />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Detail Dialog */}
      <Dialog open={detail} onClose={() => setDetail(false)} maxWidth="md" fullWidth>
        {selected && (() => {
          const stepIdx = getStepIndex(selected.status);
          const arr = getArrivalInfo(selected);
          return (
            <>
              <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6">Shipment #{selected.id}</Typography>
                  <Chip label={(selected.status || 'pending').replace(/_/g, ' ')}
                    color={STATUS_CHIP_COLOR[selected.status] || 'default'}
                    sx={{ textTransform: 'capitalize' }} />
                </Box>
              </DialogTitle>
              <DialogContent dividers>
                {/* Progress Stepper */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>Shipment Progress</Typography>
                  <Stepper activeStep={stepIdx} alternativeLabel>
                    {STEPS.map((label, i) => (
                      <Step key={label} completed={i < stepIdx}>
                        <StepLabel StepIconComponent={() => (
                          <Box sx={{
                            width: 32, height: 32, borderRadius: '50%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: i <= stepIdx ? 'primary.main' : 'grey.300',
                            color: 'white', fontSize: 14,
                          }}>
                            {STEP_ICONS[i]}
                          </Box>
                        )}>
                          {label}
                        </StepLabel>
                      </Step>
                    ))}
                  </Stepper>
                </Box>

                {arr && (
                  <Alert severity={arr.color === 'error' ? 'error' : arr.color === 'success' ? 'success' : 'info'}
                    sx={{ mb: 2 }}>
                    <strong>{arr.label}</strong>
                    {selected.expected_arrival && ` — Expected: ${formatDate(selected.expected_arrival)}`}
                  </Alert>
                )}

                <Grid container spacing={2}>
                  {[
                    ['Product',          selected.product],
                    ['Container No.',    selected.container_number],
                    ['Vessel Name',      selected.vessel_name],
                    ['BL Number',        selected.bl_number],
                    ['Shipping Line',    selected.shipping_line],
                    ['Container Type',   selected.container_type],
                    ['Port of Loading',  selected.port_of_loading],
                    ['Port of Discharge',selected.port_of_discharge],
                    ['Expected Departure',formatDate(selected.expected_departure)],
                    ['Expected Arrival', formatDate(selected.expected_arrival)],
                    ['Actual Departure', formatDate(selected.actual_departure)],
                    ['Actual Arrival',   formatDate(selected.actual_arrival)],
                    ['Transit Days',     selected.transit_days],
                    ['Temperature',      selected.temperature_control],
                  ].filter(([,v]) => v).map(([label, value]) => (
                    <Grid item xs={6} key={label}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight="medium">{value}</Typography>
                    </Grid>
                  ))}
                </Grid>

                {selected.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body2">{selected.notes}</Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDetail(false)}>Close</Button>
                {getBlDocUrl(selected) && (
                  <Button variant="outlined" color="success" startIcon={<Download />}
                    onClick={() => window.open(getBlDocUrl(selected), '_blank')}>
                    Download BL
                  </Button>
                )}
                {selected.tracking_url && (
                  <Button variant="contained" startIcon={<LocationOn />}
                    onClick={() => window.open(selected.tracking_url, '_blank')}>
                    Track Online
                  </Button>
                )}
              </DialogActions>
            </>
          );
        })()}
      </Dialog>
    </Container>
  );
};

export default BuyerShipping;
